import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useAnalystBench,
  getAllInstitutions,
  type BenchInstitution,
} from '../../context/AnalystBenchContext';
import './BenchHeader.css';

export default function BenchHeader() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    selectedInstitution,
    setSelectedInstitution,
  } = useAnalystBench();

  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Show all institutions when no query, filter when typing
  const allInstitutions = getAllInstitutions();
  const filteredInstitutions = searchQuery.trim() === ''
    ? allInstitutions
    : allInstitutions.filter(inst =>
        inst.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inst.id.toLowerCase().includes(searchQuery.toLowerCase())
      );

  const handleInstitutionSelect = (institution: BenchInstitution) => {
    setSelectedInstitution(institution);
    navigate(`/analyst-bench/${institution.id}`);
    setSearchQuery('');
    setShowDropdown(false);
  };

  const handleClearSelection = () => {
    setSelectedInstitution(null);
    setSearchQuery('');
    navigate('/analyst-bench');
  };

  const handleGoHome = () => {
    setSelectedInstitution(null);
    setSearchQuery('');
    setShowDropdown(false);
    navigate('/analyst-bench');
  };

  return (
    <header className="bench-header">
      <div className="bench-header-left">
        {/* Logo / Title */}
        <button className="bench-logo" onClick={handleGoHome}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
            <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
            <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
            <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
          </svg>
          <span>{t('header.title')}</span>
        </button>

        {/* Search Bar */}
        <div className="bench-search-wrapper" ref={searchRef}>
          <svg className="bench-search-icon" width="18" height="18" viewBox="0 0 20 20" fill="none">
            <path d="M17.5 17.5L13.875 13.875M15.8333 9.16667C15.8333 12.8486 12.8486 15.8333 9.16667 15.8333C5.48477 15.8333 2.5 12.8486 2.5 9.16667C2.5 5.48477 5.48477 2.5 9.16667 2.5C12.8486 2.5 15.8333 5.48477 15.8333 9.16667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>

          {selectedInstitution ? (
            <div className="bench-search-selected">
              <span className="selected-name">{selectedInstitution.name}</span>
              <button
                className="selected-clear"
                onClick={handleClearSelection}
                aria-label={t('header.clearSelection')}
              >
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          ) : (
            <input
              type="text"
              className="bench-search-input"
              placeholder={t('header.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setShowDropdown(true)}
            />
          )}

          {showDropdown && !selectedInstitution && (
            <div className="bench-search-dropdown">
              {filteredInstitutions.length === 0 ? (
                <div className="dropdown-empty">{t('header.noInstitutions')}</div>
              ) : (
                filteredInstitutions.map(inst => (
                  <button
                    key={inst.id}
                    className="dropdown-item"
                    onClick={() => handleInstitutionSelect(inst)}
                  >
                    <div className="dropdown-item-name">{inst.name}</div>
                    <div className="dropdown-item-meta">{inst.id}</div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      <div className="bench-header-right">
        {/* Home Button - only show when an institution is selected */}
        {selectedInstitution && (
          <button className="bench-home-button" onClick={handleGoHome} title={t('header.backHome')}>
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
              <path d="M3.33333 10L10 3.33333L16.6667 10M5 8.33333V15.8333C5 16.2754 5.21071 16.6993 5.58579 17.0118C5.96086 17.3244 6.46957 17.5 7 17.5H13C13.5304 17.5 14.0391 17.3244 14.4142 17.0118C14.7893 16.6993 15 16.2754 15 15.8333V8.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>
    </header>
  );
}
